import Text from 'components/Text';
import { CALENDAR_ITEM_TYPES } from 'features/appointment/services/constants';
import { ICalendarDndData, ICalendarViewColItem, ICalendarViewRowItem, ICalendarViewRowSpanInfoItem } from 'features/appointment/services/types/calendar';
import { FORMAT_TIME } from 'features/appointment/utils/format';
import moment, { Moment } from 'moment';
import { memo, useCallback, useMemo } from 'react';
import CalendarStyled, { fixedStyle } from '../styles';
import CalendarItem from './CalendarItem';
import Droppable from './DnD/Droppable';

const TIME_VALUE_FORMAT = 'HH:mm';

export const convertCalendarTime = (rowTime: Moment) => {
  const minute = rowTime.minutes();
  if (minute > 0) return rowTime.format('mm');
  return rowTime.format(FORMAT_TIME);
};

type Props = {
  data: ICalendarViewRowItem,
  rowsSpanInfo: ICalendarViewRowSpanInfoItem[],
  isSingleCol?: boolean;
  distance: number;
  index: number;
  v2?: boolean;
};

const CalendarViewRow = ({ data, rowsSpanInfo, index, v2 }: Props) => {
  const { colData, rowTime } = data;

  const renderColumn = useCallback((col: ICalendarViewColItem, index: number) => {
    const dropId = data.id + '/' + col.id;
    const dropData: ICalendarDndData = { rowId: data.id, colData: col, rowTime: data.rowTime, blockDrop: false };
    let disable = false;
    if (col.headerData.workingTime) {
      const colTime = moment(col.rowTime.format(TIME_VALUE_FORMAT), TIME_VALUE_FORMAT);
      const startWorkingDate = moment(col.headerData.workingTime?.startTime, 'HH:mm');

      const isBetweenWorkingHour = colTime.isSame(startWorkingDate) || colTime.isBetween(
        startWorkingDate,
        moment(col.headerData.workingTime?.endTime, 'HH:mm')
      );
      disable = !col.headerData.workingTime?.enable || !isBetweenWorkingHour;
    }

    const orderGroup = rowsSpanInfo.find(it =>
      rowTime.isSameOrBefore(it?.rowEnd.value) &&
      it.colId === col.id &&
      col.data.some(i => it.orderIds.includes(i?.id?.toString())),
    );

    const isExact = !!orderGroup && orderGroup.rowStart.id === data.id;

    if (isExact) {
      const includeBlockBreakHours = col.data.some(o => o.type === CALENDAR_ITEM_TYPES.BLOCK_HOURS || o.type === CALENDAR_ITEM_TYPES.BREAK_STAFF);
      return (
        <CalendarStyled.TD disable={includeBlockBreakHours} key={col.id + 'col'} rowSpan={orderGroup?.rowSpan || 1} className={`${col.rowTime.format('DD-MM-YYYY HH:mm')} CalendarStyled_isExact ${dropId} ${col.id} ${index === 0 ? 'first-col' : ''} `} >
          <Droppable headerData={col.headerData} disable={disable} blockDrop={includeBlockBreakHours} rowSpan={orderGroup?.rowSpan || 1} key={dropId} id={dropId} data={dropData}>
            <CalendarItem v2={v2} data={col.data} headerData={col.headerData} />
          </Droppable>
        </CalendarStyled.TD>
      );
    }
    const isBefore = !!orderGroup;

    if (isBefore) return <CalendarStyled.TD disable={disable} key={col.id + 'col'} className={`${col.rowTime.format('DD-MM-YYYY HH:mm')} CalendarStyled_isBefore ${dropId} ${col.id} ${index === 0 ? 'first-col' : ''}`} >
      <Droppable timeView={convertCalendarTime(col.rowTime)} key={dropId} id={dropId} data={dropData} /></CalendarStyled.TD>;

    return (
      <CalendarStyled.TD disable={disable} key={col.id} className={`${col.rowTime.format('DD-MM-YYYY HH:mm')} CalendarStyled_none ${dropId} ${col.id} ${index === 0 ? 'first-col' : ''}`}>
        <Droppable timeView={convertCalendarTime(col.rowTime)} headerData={col.headerData} disable={disable} blockDrop={disable} key={dropId} id={dropId} data={dropData} />
      </CalendarStyled.TD>
    );
  }, [data, rowsSpanInfo, v2]);

  const time = useMemo(() => {
    const minute = data.rowTime.minutes();
    if (minute > 0) return data.rowTime.format('mm');
    return data.rowTime.format(FORMAT_TIME);
  }, [data]);

  return (
    <CalendarStyled.TRow className={`${index === 0 ? 'first-row' : ''}`}>
      <CalendarStyled.TD fixed time style={fixedStyle}>
        <div className="box-time">
          <Text className='text-time' textAlign='right' variant='CAPTION_3' style={{ color: 'blue' }} >{time}</Text>
        </div>
      </CalendarStyled.TD>
      {colData.length > 0 ? colData.map(renderColumn) : <CalendarStyled.TD className='first-col' />}
    </CalendarStyled.TRow>
  );
};

export default memo(CalendarViewRow);
